import React, { useEffect, useState } from 'react';
import { SearchBarComponent } from './searchBar.component';
import { ClBaseSearch, FindedElementOnSearch, FmDocSearch, FmEventSearch, FmNoteSearch, PlActionSearch, RpDocSearch, RpNoteSearch, RpTaskSearch, SearchApiResponse, TYPE_OF_SEARCH } from 'interfaces/api_deep_search.interfaces';
import { DeepSearchService } from 'services/deep_search.service';
import { useDispatch, useSelector } from 'react-redux';
import { PaginationComponent, RootState } from '@umanforge/common';
import { FmEventCardComponent } from './flashmeeting/fmEventCard.component';
import { FmNoteCardComponent } from './flashmeeting/fmNoteCard.component';
import { FmDocCardComponent } from './flashmeeting/fmDocCard.component';
import { RpCardComponent } from './resolution/rpCard.component';
import { RpNoteCardComponent } from './resolution/rpNoteCard.component';
import { RpDocCardComponent } from './resolution/rpDocCard.component';
import { PlActionCardComponent } from './planiteam/plActionCard.component';
import { useTranslation } from 'react-i18next';
import { ClFileCardComponent } from './cloud/clFileCard.component';
import { Loading } from 'components/loading.component';

const WAIT_TIME: number = 650;
const DEFAULT_DATAS: SearchApiResponse = { total: 0, datas: [] };


const SearchComponent: React.FC = ( { } ) => {
  const { t } = useTranslation();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();

  const [ controller, setController ] = useState<AbortController | null>( null );

  const [ searchedValue, setSearchedValue ] = useState<string>( '' );
  const [ findedDatas, setFindedDatas ] = useState<SearchApiResponse>( DEFAULT_DATAS );
  const [ page, setPage ] = useState<number>( 0 );
  const [ isLoading, setIsLoading ] = useState<boolean>( false );
  const [ isTyping, setIsTyping ] = useState<boolean>( false );
  const [ step ] = useState<number>( 100 );

  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );

  useEffect( () => {
    setIsFirstRender( false );
  }, [] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const timeOutId = setTimeout( () => { startController(); }, WAIT_TIME );
      setIsTyping( true );
      return () => { clearTimeout( timeOutId ); };
    }
  }, [ searchedValue ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      startController();
    }
  }, [ page, step ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      searched();
    }
  }, [ controller ] );

  const startController = () => {
    setIsTyping( false );
    if ( controller ) {
      controller.abort();
    }
    setController( new AbortController() );
  };

  const searched = async () => {
    if ( controller ) {
      let result = DEFAULT_DATAS;
      if ( searchedValue != '' ) {
        setIsLoading( true );
        result = await DeepSearchService.searched( searchedValue, page + 1, step, token, dispatch, undefined, undefined, controller );
      }
      setController( null );
      if ( result.total > 0 && result.datas.length == 0 ) {
        setPage( Math.ceil( result.total / step ) - 1  );
      } else {
        setIsLoading( false );
        setFindedDatas( result );
      }
    }
  };

  const findedComponent = ( data: FindedElementOnSearch ) => {
    let result = ( <></> );
    if ( data.application === 'FLASHMEETING' && data.type === TYPE_OF_SEARCH.TASK ) {
      result = ( <FmEventCardComponent key={ data.index } event={ data as FmEventSearch } /> );
    } else if ( data.application === 'FLASHMEETING' && data.type === TYPE_OF_SEARCH.NOTE ) {
      result = ( <FmNoteCardComponent key={ data.index } note={ data as FmNoteSearch } /> );
    } else if ( data.application === 'FLASHMEETING' && data.type === TYPE_OF_SEARCH.DOC ) {
      result = ( <FmDocCardComponent key={ data.index } doc={ data as FmDocSearch } /> );
    } else if ( data.application === 'RESOLUTION' && data.type === TYPE_OF_SEARCH.TASK ) {
      result = ( <RpCardComponent key={ data.index } rp={ data as RpTaskSearch } /> );
    } else if ( data.application === 'RESOLUTION' && data.type === TYPE_OF_SEARCH.NOTE ) {
      result = ( <RpNoteCardComponent key={ data.index } note={ data as RpNoteSearch } /> );
    } else if ( data.application === 'RESOLUTION' && data.type === TYPE_OF_SEARCH.DOC ) {
      result = ( <RpDocCardComponent key={ data.index } doc={ data as RpDocSearch } /> );
    } else if ( data.application === 'PLANITEAM' && data.type === TYPE_OF_SEARCH.TASK ) {
      result = ( <PlActionCardComponent key={ data.index } action={ data as PlActionSearch } /> );
    } else if ( data.application === 'CLOUD' && data.type === TYPE_OF_SEARCH.DOC ) {
      result = ( <ClFileCardComponent key={ data.index } doc={ data as ClBaseSearch } /> );
    }

    return result;
  };

  return (
    <div className="flex flex-col w-full items-center gap-6 mb-6 px-[10%]">
      <div className='w-1/2 min-w-[340px]'>
        <SearchBarComponent
          searchedValue={ searchedValue }
          setSearchedValue={ setSearchedValue }
        />
      </div>
      {
        isLoading ?
          <Loading />
          :
          <>
            {
              ( findedDatas.total > 0 && findedDatas.datas.length > 0 ) ?
                <div className='flex flex-wrap gap-5'>
                  {
                    findedDatas.datas.map( data => {
                      return findedComponent( data );
                    } )
                  }
                </div>
                :
                <>
                  {
                    searchedValue != '' && !isTyping && (
                      <span>{ t( 'common:no_data' ) }</span>
                    )
                  }
                </>
            }
            {
              findedDatas.total > step && (
                <PaginationComponent
                  page={ page }
                  step={ step }
                  total={ findedDatas.total }
                  setPage={ setPage }
                />
              )
            }
          </>
      }
    </div>
  );
};

export { SearchComponent };
